import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Flex,
  Form,
  Input,
  NormalizedException,
} from "@songtradr-sso/shared/frontend";
import {
  A,
  Alert,
  Body,
  Button,
  ButtonLoadingIcon,
  Divider,
  Heading,
  MassiveMusicHorizontal,
} from "@songtradr/design-language";
import { PageContainer } from "../../components/page-container/PageContainer";
import { useBfCacheRestore } from "../../shared/hooks/use-bf-cache-restore";
import {
  FormFields,
  Links,
  routes,
  schema,
  text,
  verifyWorkspace,
} from "./WorkspaceCapture.Parts";
import { HelpPanel } from "./help-panel/HelpPanel";

export const WorkspaceCapture = () => {
  const formMethods = useForm<FormFields>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
  });

  const {
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = formMethods;
  useBfCacheRestore(reset); // Reset the form when page is loaded from bfcache...

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      await verifyWorkspace(data.workspace);
    } catch (err) {
      setError("root", { message: new NormalizedException(err).message });
    }
  };

  return (
    <PageContainer header={<MassiveMusicHorizontal />}>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <Heading variant="section">{text.title}</Heading>
        <Input
          id={text.shared.inputs.workspace.id}
          label={text.shared.inputs.workspace.label}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        {errors.root?.message && (
          <Alert
            title={text.alerts.error}
            message={errors.root?.message}
            dismissible
          />
        )}
        <Button $variant="important" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <ButtonLoadingIcon /> : text.button}
        </Button>
        <Links>
          <Divider />
          <Flex $gap="0.25rem">
            <Body>{text.links.defaultSignIn.text}</Body>
            <A as={Link} to={routes.signIn}>
              {text.links.defaultSignIn.trigger}
            </A>
          </Flex>
          <Flex $gap="0.25rem">
            <Body>{text.links.helpPanel.text}</Body>
            <HelpPanel
              trigger={
                <A as={Link} to="#">
                  {text.links.helpPanel.trigger}
                </A>
              }
            />
          </Flex>
        </Links>
      </Form>
    </PageContainer>
  );
};
